<template>
  <div class="size-split-variant-wrapper">
    <SizeSelection
      v-model="selectedSizeId"
      v-model:touched="touched"
      v-bind="sizeProps"
      :product-id="id"
      :name="`cta-sizes-${uniqueId}-${id}`"
      :is-size-chart
      :size-chart-img
      :size-chart-id
      :brand-name
      :type-name
      :error-text-active
      class="margin-bottom-xs"
      :label="sizeLabel"
    />

    <ProductType
      v-if="types.length"
      :id="id"
      v-model="selectedTypeId"
      :types="types"
      :label="typeLabel"
      :name="`cta-types-${uniqueId}-${id}`"
      :error-text-active
      class="margin-bottom-s"
    />
  </div>
</template>

<script setup>
/* istanbul ignore file */
import { ref, computed, watch, useId } from 'vue';

import { SizeSelection } from '@nuxt-common/media-card-large/app/modules/size';
import { ProductType } from '@nuxt-common/media-card-large/app/modules/type';

import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { useTraitAnalyticsData } from '@nuxt-common/media-card-large/app/composables/useTraitAnalyticsData';
import { useType } from '~/modules/type/composables/useType';
import useSize from '~/modules/size/composables/useSize';
import useAvailabilityStore from '~/stores/useAvailabilityStore';

import { useBrand } from '~/composables/useBrand';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useProtectionPlanAnalytics } from '~/modules/worry-no-more/analytics/useWnmAnalytics';
import useSizeChart from '~/composables/useSizeChart';
import useStickyAtbAnalytics from '~/modules/sticky-product-traits/composables/useStickyAtbAnalytics';
import { useTraitsErrorActive } from '~/modules/sticky-product-traits/composables/context-providers/useTraitsErrorActive';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: true,
  },
  productPricingState: {
    type: Array,
    required: true,
  },
});

const uniqueId = useId();

const { isMcom } = useBrand();
const { brandName, typeName } = useCommonProductGetters();
const { getExtraData } = useProtectionPlanAnalytics();
const { extraTraitsData } = useStickyAtbAnalytics();
const { errorTextActive } = useTraitsErrorActive();
const { label: sizeLabel, sizeProps, selectedSizeId } = useSize();
const { selectedUpcId } = useUpc(props.id);

const availabilityData = computed(() => useAvailabilityStore().data);

const touched = ref(false);

const { sizeChartImage: sizeChartImg, isSizeChart, sizeChartId } = useSizeChart();

const { fireTagOnChange } = useTraitAnalyticsData({
  isConsolidatedMaster: false,
  isMcom,
  product: props.product,
  isSplitThirdVariant: true,
});

const { types, label: typeLabel, selectedTypeId } = useType();

watch(selectedTypeId, () => {
  const selectedSddData = availabilityData.value?.upcNumberSddMap || {};

  fireTagOnChange({
    selectedUpcId: selectedUpcId.value,
    isVariant: true,
    productPricingState: props.productPricingState,
    selectedSddData,
    extraData: {
      ...getExtraData(),
      ...extraTraitsData('type'),
    },
  });
});
</script>
